import { Placement } from '@popperjs/core/lib/enums';
import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { useState, useRef, useEffect, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircleOutline } from 'react-ionicons';
import { usePopper } from 'react-popper';

export const useTooltip = () => {
  const triggerEltRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  return {
    triggerEltRef,
    isVisible,
    handleEnter: () => setIsVisible(true),
    handleLeave: () => setIsVisible(false),
  };
};

type TooltipProps = {
  triggerEltRef: RefObject<HTMLDivElement>;
  isVisible: boolean;
  title?: string;
  body?: React.ReactNode;
  placement?: Placement;
  arrow?: boolean;
  tooltipOffset?: number;
};

export const Tooltip = ({
  triggerEltRef,
  isVisible,
  title,
  body,
  placement = 'bottom-start',
  arrow = false,
  tooltipOffset = 8,
}: TooltipProps) => {
  const popperEltRef = useRef<HTMLDivElement>(null);
  const arrowEltRef = useRef<HTMLDivElement>(null);

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (popperEltRef.current) setPopperElement(popperEltRef.current);
    if (arrowEltRef.current) setArrowElement(arrowEltRef.current);
  }, [isVisible]);

  const { styles, attributes } = usePopper(
    triggerEltRef.current,
    popperElement,
    {
      placement,
      modifiers: [
        { name: 'offset', options: { offset: [0, tooltipOffset] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        { name: 'preventOverflow', options: { boundary: 'clippingParents' } },
        { name: 'arrow', options: { element: arrowElement } },
      ],
    },
  );

  const currentPlacement =
    attributes.popper?.['data-popper-placement'] ?? placement;

  const arrowOffsetStyle: React.CSSProperties = {
    ...styles.arrow,
    width: '14px',
    height: '14px',
    overflow: 'hidden',
    transform: 'scaleX(1.25) rotate(45deg)',
    pointerEvents: 'none',
    ...(currentPlacement.startsWith('top') && { bottom: '-7px', left: '50%' }),
    ...(currentPlacement.startsWith('bottom') && { top: '-7px', left: '50%' }),
    ...(currentPlacement.startsWith('left') && { right: '-7px', top: '50%' }),
    ...(currentPlacement.startsWith('right') && { left: '-7px', top: '50%' }),
  };

  if (!isVisible) return null;

  return createPortal(
    <div
      ref={popperEltRef}
      style={styles.popper}
      {...attributes.popper}
      className="z-[50] max-w-[328px] rounded-lg bg-background-base-surface-0 p-5 text-content-base-subdued shadow-elevation3"
    >
      {title && (
        <p className="text-base font-bold text-content-base-default">{title}</p>
      )}
      {body && <p className="whitespace-normal text-sm font-medium">{body}</p>}
      {arrow && (
        <div
          ref={arrowEltRef}
          style={arrowOffsetStyle}
          className="absolute bg-background-base-surface-0 shadow-sm"
          {...attributes.arrow}
        />
      )}
    </div>,
    document.body,
  );
};

interface NetworkMonitoringLabelProps {
  mns: SelectedPackages;
}

export const NetworkMonitoringLabel = ({
  mns,
}: NetworkMonitoringLabelProps) => {
  const { triggerEltRef, isVisible, handleEnter, handleLeave } = useTooltip();

  return (
    <>
      <div
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className="inline-flex items-center gap-1 whitespace-nowrap"
      >
        <span>Network&nbsp;monitoring</span>
        <span ref={triggerEltRef}>
          <HelpCircleOutline color="currentColor" width="24px" height="24px" />
        </span>
      </div>

      <Tooltip
        triggerEltRef={triggerEltRef}
        isVisible={isVisible}
        title="Network monitoring"
        body={
          mns.essential ? (
            <>
              Granite’s <b>MNS Essentials</b> bundle provides end-to-end network
              management. Includes ping monitoring for basic network performance
              insights and the edgeboot service bundle.
            </>
          ) : (
            <>
              Granite’s <b>MNS Enhanced</b> bundle provides end-to-end network
              management. Includes advanced network telemetry for detailed
              performance insights, API access, and the edgeboot service bundle.
            </>
          )
        }
      />
    </>
  );
};
